import { useRef } from 'react';
import type { PAGE_POSITIONS, PageType } from 'types';
import type { ProductListItemInternal } from 'types/capi/shared';

import CarouselScroller from '~/components/CarouselScroller';
import NotificationTooltip from '~/components/NotificationTooltip';
import ProductCard from '~/components/ProductCard';
import useHasScrolledIntoView from '~/hooks/useHasScrolledIntoView';

const PRODUCTS_PER_DESKTOP_ROW = 4;

type Props = {
  allowNotifications: boolean;
  description: string;
  modulePosition?: number;
  moduleType: string;
  overrideInteractionPagePosition?: boolean;
  pagePosition: PAGE_POSITIONS;
  pageType: PageType;
  products: ProductListItemInternal[];
  showBackground: boolean;
  title: string;
};

export default function PopularReleasesCarousel({
  allowNotifications,
  description,
  modulePosition,
  moduleType,
  overrideInteractionPagePosition = false,
  pagePosition,
  pageType,
  products,
  showBackground,
  title
}: Props) {
  const productsRef = useRef<HTMLDivElement>(null);
  const productsInView = useHasScrolledIntoView(productsRef);

  return (
    <div
      className={`w-full ${showBackground ? 'bg-black py-6 text-white md:py-10' : ''}`}
    >
      <div
        className={`content-width flex h-fit flex-col items-center justify-start gap-6 ${pageType === 'plp' ? '' : 'md:gap-10'}`}
      >
        <div className="flex w-full flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div className="flex flex-col gap-2">
            <span
              className={
                pageType === 'plp'
                  ? 'text-title-md leading-none'
                  : 'text-title-xl'
              }
            >
              {title}
            </span>
            <span className="text-card-sm">{description}</span>
          </div>
          {allowNotifications && (
            <NotificationTooltip
              className={`w-full shrink-0 xs:w-[42vw] md:w-[264px] ${products.length < PRODUCTS_PER_DESKTOP_ROW ? 'md:hidden' : ''}`}
              isRevealed={productsInView}
            />
          )}
        </div>
        <div className="w-full" ref={productsRef}>
          <CarouselScroller slidesClassName="gap-2 md:gap-6">
            {products.map((product, productIndex) => (
              <ProductCard.Internal
                key={`product-${productIndex}`}
                product={product}
                padImage
                className="w-[42vw] flex-none md:w-[264px]"
                allowNotifications={allowNotifications}
                pageType={pageType}
                imageLoading="eager"
                cardSize="small"
                modulePosition={modulePosition}
                moduleType={moduleType}
                showNotificationCount={true}
                pagePosition={pagePosition}
                position={productIndex + 1}
                overrideInteractionPagePosition={
                  overrideInteractionPagePosition
                }
              />
            ))}
          </CarouselScroller>
        </div>
      </div>
    </div>
  );
}
